"use client";

import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { XIcon } from "@phosphor-icons/react/ssr";
import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import Pressable from "~/componentsDS/Pressable";
import { cn } from "~/lib/utils";
import { useClientMediaQuery } from "~/hooks/useClientMediaQuery";
import { AnalyticEvent } from "./InteractiveTimeline/types";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { useTrackEvent } from "~/hooks/useTrackEvent";

type Direction = "normal" | "reverse";

type TooltipVariant = "default" | "white";

type PulseInfoButtonProps = {
    title: ReactNode;
    analyticsTitle?: string;
    description: string;
    direction?: Direction;
    size?: "small" | "medium";
    analyticEvent?: AnalyticEvent;
    tooltipVariant?: TooltipVariant;
};

const defaultAnalyticEvent = {
    event_name: GTM_EVENT_NAMES.LP_INTERACTION,
    widget: "our_science",
};

type MobilePanelState = "closed" | "open" | "closing";

const absoluteCenterCn =
    "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2";

const MOBILE_ANIMATION_MS = 300;

const PulseInfoButton = ({
    title,
    analyticsTitle,
    description,
    direction = "normal",
    size = "small",
    analyticEvent = defaultAnalyticEvent,
    tooltipVariant = "default"
}: PulseInfoButtonProps) => {
    const isWhiteTooltip = tooltipVariant === "white";
    const isMobile = useClientMediaQuery("(max-width: 767px)");
    const [mobilePanelState, setMobilePanelState] = useState<MobilePanelState>("closed");
    const [isMounted, setIsMounted] = useState(false);
    const sendEvent = useTrackEvent();

    const rootRef = useRef<HTMLDivElement | null>(null);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isMobilePanelVisible = mobilePanelState !== "closed";
    const isMobilePanelClosing = mobilePanelState === "closing";

    const clearCloseTimeout = useCallback(() => {
        if (!closeTimeoutRef.current) return;

        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
    }, []);

    useEffect(() => {
        setIsMounted(true);

        return clearCloseTimeout;
    }, [clearCloseTimeout]);

    const handleOpen = useCallback(() => {
        if (!isMobile || mobilePanelState !== "closed") return;

        clearCloseTimeout();
        setMobilePanelState("open");

        if (!analyticEvent || !analyticsTitle) return;

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: analyticEvent.event_name,
                widget: analyticEvent.widget,
                meta_data: {
                    click_text: analyticsTitle,
                },
            },
        });
    }, [
        analyticEvent,
        analyticsTitle,
        clearCloseTimeout,
        isMobile,
        mobilePanelState,
        sendEvent,
    ]);

    const handleClose = useCallback(() => {
        if (!isMobile || mobilePanelState !== "open") return;

        clearCloseTimeout();
        setMobilePanelState("closing");

        closeTimeoutRef.current = setTimeout(() => {
            setMobilePanelState("closed");
            closeTimeoutRef.current = null;
        }, MOBILE_ANIMATION_MS);
    }, [clearCloseTimeout, isMobile, mobilePanelState]);

    const handleToggle = useCallback(() => {
        if (!isMobile) return;

        if (mobilePanelState === "open") {
            handleClose();
            return;
        }

        handleOpen();
    }, [handleClose, handleOpen, isMobile, mobilePanelState]);

    useEffect(() => {
        if (!isMobile || mobilePanelState !== "open") return;

        const handlePointerDown = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node | null;

            if (!target) return;
            if (rootRef.current?.contains(target)) return;

            handleClose();
        };

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("touchstart", handlePointerDown);

        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("touchstart", handlePointerDown);
        };
    }, [handleClose, isMobile, mobilePanelState]);

    useEffect(() => {
        if (isMobile) return;

        clearCloseTimeout();
        setMobilePanelState("closed");
    }, [clearCloseTimeout, isMobile]);

    return (
        <>
            {isMounted && isMobile && isMobilePanelVisible && createPortal(
                <div
                    className={cn(
                        "fixed inset-0 z-[47] bg-[rgba(0,0,0,0.25)]",
                        isMobilePanelClosing
                            ? "animate-[fade-out-linear_300ms]"
                            : "animate-[fade-in_300ms]"
                    )}
                    onClick={handleClose}
                    aria-hidden="true"
                />,
                document.body
            )}

            <Pressable
                ref={rootRef}
                onActivate={handleToggle}
                className={cn(
                    "relative flex items-center group",
                    isMobile && "cursor-pointer",
                    direction === "reverse" && "flex-row-reverse"
                )}
            >
                <div
                    className={cn(
                        "absolute md:top-[14px] md:-translate-y-full flex flex-col items-center",
                        direction === "reverse" ? "md:left-[-136px]" : "md:right-[-136px]",
                        "md:transition-all md:duration-300 md:ease-out",
                        "max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0 max-md:z-[48]",
                        "opacity-0 md:pointer-events-none md:group-hover:opacity-100 md:group-hover:pointer-events-auto",
                        isWhiteTooltip && "md:[filter:drop-shadow(0_4px_16px_rgba(0,0,0,0.08))]",
                        isMobile
                            ? isMobilePanelVisible
                                ? cn(
                                    "pointer-events-auto opacity-100",
                                    isMobilePanelClosing
                                        ? "animate-[fade-out-down_300ms_ease-out]"
                                        : "animate-[fade-in-up_300ms_ease-out]"
                                )
                                : "opacity-0 pointer-events-none"
                            : ""
                    )}
                >
                    <div
                        className={cn(
                            "md:w-[320px] text-primary500 px-12 pt-5 pb-12 md:p-4 rounded-t-2xl md:rounded-b-2xl relative max-md:shadow-[0_-3.998px_16.991px_0_rgba(0,0,0,0.25)]",
                            isWhiteTooltip ? "bg-pureWhite" : "bg-primary100"
                        )}
                        onClick={(event) => event.stopPropagation()}
                        role="presentation"
                    >
                        {isMobile && (
                            <button
                                type="button"
                                aria-label="Close tooltip"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleClose();
                                }}
                                className="absolute top-[6px] right-3 flex items-center justify-center size-8 text-[24px] text-gray400"
                            >
                                <XIcon size={20} weight="light" />
                            </button>
                        )}

                        <div className="md:hidden w-[124px] h-[5px] rounded-full bg-gray300 mx-auto mb-6" />

                        <Paragraph size="lg" weight="500" className="max-md:text-[32px] max-md:font-light mb-1 pr-6">
                            {title}
                        </Paragraph>
                        <Paragraph>{description}</Paragraph>
                    </div>

                    <svg
                        className="w-[33px] h-[17px] mt-[-1px] max-md:hidden"
                        viewBox="0 0 33 17"
                        fill="none"
                    >
                        <path
                            d="M32.2266 0.160156L19.055 13.3317C17.4929 14.8938 14.9602 14.8938 13.3981 13.3317L0.226562 0.160156H32.2266Z"
                            className={isWhiteTooltip ? "fill-pureWhite" : "fill-[#E9EDF7]"}
                        />
                    </svg>
                </div>

                <Heading
                    as="p"
                    size={size === "small" ? "2xs" : "sm"}
                    weight={size === "small" ? "400" : "300"}
                    className="max-md:text-[18px] max-md:font-normal text-primary500"
                >
                    {title}
                </Heading>

                <div
                    className={cn(
                        "h-[1px] w-[90px] bg-[repeating-linear-gradient(to_right,_#3654AE_0px,_#3654AE_2px,_transparent_2px,_transparent_4px)]",
                        direction === "reverse" ? "ml-[-10px] mr-[6px]" : "ml-[6px] mr-[-10px]",
                        size === "small" ? "md:w-[70px]" : "md:w-[118px]",
                    )}
                />

                <div className="inline-flex justify-center items-center relative w-12 h-12">
                    <div className="size-6 rounded-full bg-[radial-gradient(circle,_#5472CC_0%,_rgba(84,114,204,0.25)_70%,_transparent_100%)] animate-[ping_2s_ease-out_infinite]" />
                    <div
                        className={cn(
                            absoluteCenterCn,
                            "size-6 rounded-full bg-[radial-gradient(circle,_#5472CC_0%,_rgba(84,114,204,0.25)_70%,_transparent_100%)]"
                        )}
                    />
                    <div
                        className={cn(
                            absoluteCenterCn,
                            "flex justify-center items-center w-[14px] h-[14px] border border-primary300 rounded-full",
                            "font-suisseBP text-[12px] font-light text-pureWhite",
                            direction === "reverse" && "pl-[1px]"
                        )}
                    >
                        +
                    </div>
                </div>
            </Pressable>
        </>
    );
};

export default PulseInfoButton;
